import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { getRemainingDescription, getTariffState } from '@entities/tariff/lib';
import { Icon, Image, UiButton, UiText } from '@shared/ui';

import cls from './ProfileSummary.module.css';
import { ProfileCard } from '../ProfileCard';

import type { Profile } from '@entities/profile';

type ProfileSummaryProps = {
    profile: Profile;
    onPaySubscriptionClick?: ({ removeProlong }: { removeProlong: boolean }) => void;
};

export const ProfileSummary = ({ profile, onPaySubscriptionClick }: ProfileSummaryProps) => {
    const { t } = useTranslation();
    const tariffState = getTariffState(profile.tariff);

    const tariffDescription =
        tariffState.type === 'remaining'
            ? getRemainingDescription(tariffState, t)
            : {
                  unlimited: t('profile.page.summary.tariff.unlimited'),
                  suspended: t('profile.page.summary.tariff.suspended'),
                  autoRenewal: t('profile.page.summary.tariff.autoRenewal'),
              }[tariffState.type];

    return (
        <ProfileCard className={cls.profileSummary}>
            <UiText tag="h1" variant="subHeading" weight="bold" className={cls.summaryTitle}>
                {t('profile.page.summary.title')}
            </UiText>
            <div className={cls.profileDetails}>
                {profile.user.avatarUrl ? (
                    <Image
                        src={profile.user.avatarUrl}
                        alt={t('profile.page.summary.avatarAlt')}
                        width={90}
                        height={90}
                        className={cls.avatar}
                    />
                ) : (
                    <span className={cls.avatarPlaceholder}>
                        <Icon iconName="profileBigSize" width={90} height={90} />
                    </span>
                )}
                <UiText weight="bold" className={cls.profileName}>
                    {profile.user.name}
                </UiText>
                <UiText variant="subPlain" className={cls.profileEmail}>
                    {profile.user.email}
                </UiText>
                <UiText variant="subPlain">
                    {t('profile.page.summary.tariff')} {profile.tariff.title}
                </UiText>
                <UiText
                    variant="caption"
                    className={cn(
                        cls.remainingTime,
                        tariffState.days < 3 && cls.remainingTimeDanger
                    )}
                >
                    {tariffDescription}
                </UiText>
            </div>
            <UiButton
                btnText={t('profile.page.summary.paySubscription')}
                onClick={() => onPaySubscriptionClick?.({ removeProlong: false })}
            />
        </ProfileCard>
    );
};
